import { useMemo } from "react";
import Chart from "react-apexcharts";

const formatPercentage = (value, decimals = 1) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "0%";
  }

  return `${numberValue.toFixed(decimals)}%`;
};

function DepartmentQualityChart({
  chartdata = {},
  chartKey = "",
  chartType = "column",
}) {
  const categories = useMemo(() => {
    return chartdata?.sortedDeptNames || [];
  }, [chartdata]);

  const series = useMemo(() => {
    return chartdata?.chartData || [];
  }, [chartdata]);

  const isAreaChart = chartType === "area";

  const options = useMemo(() => {
    return {
      chart: {
        type: isAreaChart ? "area" : "bar",

        animations: {
          enabled: true,

          easing: "easeinout",

          speed: isAreaChart ? 1000 : 850,

          animateGradually: {
            enabled: true,
            delay: isAreaChart ? 120 : 90,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 500,
          },
        },

        toolbar: {
          show: false,
        },

        zoom: {
          enabled: false,
        },

        parentHeightOffset: 0,

        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },

      colors: ["#22c55e", "#ef4444"],

      plotOptions: {
        bar: {
          horizontal: false,

          columnWidth: "46%",

          borderRadius: 7,

          borderRadiusApplication: "end",

          dataLabels: {
            position: "top",
          },
        },
      },

      stroke: {
        show: true,

        curve: isAreaChart ? "smooth" : "straight",

        width: isAreaChart ? 3 : 0,

        lineCap: "round",
      },

      fill: isAreaChart
        ? {
            type: "gradient",

            gradient: {
              shade: "light",

              type: "vertical",

              shadeIntensity: 0.35,

              inverseColors: false,

              opacityFrom: 0.45,

              opacityTo: 0.03,

              stops: [0, 65, 100],
            },
          }
        : {
            opacity: 1,
          },

      markers: {
        size: isAreaChart ? 4 : 0,

        strokeWidth: 2,

        strokeColors: "#ffffff",

        hover: {
          size: 7,

          sizeOffset: 2,
        },
      },

      /**
       * Column chart:
       * Show actual percentage above every column.
       *
       * Area chart:
       * Hide data labels.
       */
      dataLabels: {
        enabled: true,

        formatter: (value) => {
          return formatPercentage(value, 1);
        },

        offsetY: -14,

        style: {
          fontSize: "10px",

          fontWeight: 700,

          colors: ["#334155"],
        },

        background: {
          enabled: false,
        },

        dropShadow: {
          enabled: false,
        },
      },

      grid: {
        borderColor: "#e8edf5",

        strokeDashArray: 4,

        padding: {
          top: !isAreaChart ? 30 : 10,

          left: 5,

          right: 5,

          bottom: 0,
        },

        xaxis: {
          lines: {
            show: false,
          },
        },

        yaxis: {
          lines: {
            show: true,
          },
        },
      },

      xaxis: {
        categories,

        axisBorder: {
          show: false,
        },

        axisTicks: {
          show: false,
        },

        tooltip: {
          enabled: false,
        },

        labels: {
          rotate: -55,

          rotateAlways: categories.length > 8,

          trim: true,

          hideOverlappingLabels: true,

          style: {
            colors: "#64748b",

            fontSize: "10px",

            fontWeight: 500,
          },
        },
      },

      yaxis: {
        min: 0,

        max: 100,

        tickAmount: 5,

        forceNiceScale: false,

        labels: {
          formatter: (value) =>
            `${Math.round(Number(value) || 0)}%`,

          style: {
            colors: "#64748b",

            fontSize: "10px",

            fontWeight: 500,
          },
        },
      },

      legend: {
        show: true,

        position: "top",

        horizontalAlign: "right",

        fontSize: "12px",

        fontWeight: 600,

        markers: {
          width: 8,

          height: 8,

          radius: 8,
        },

        itemMargin: {
          horizontal: 10,

          vertical: 4,
        },
      },

      tooltip: {
        shared: true,

        intersect: false,

        theme: "light",

        followCursor: false,

        marker: {
          show: true,
        },

        y: {
          formatter: (value) => {
            return formatPercentage(value, 2);
          },

          title: {
            formatter: (seriesName) => `${seriesName}: `,
          },
        },
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "lighten",

            value: 0.05,
          },
        },

        active: {
          allowMultipleDataPointsSelection: false,

          filter: {
            type: "darken",

            value: 0.08,
          },
        },
      },

      noData: {
        text: "No quality data available",

        align: "center",

        verticalAlign: "middle",

        style: {
          color: "#64748b",

          fontSize: "13px",
        },
      },
    };
  }, [categories, isAreaChart]);

  if (!series.length) {
    return (
      <div className="chart-empty-state">
        No quality percentage data available.
      </div>
    );
  }

  return (
    <div
      className={`department-quality-chart-wrapper ${
        isAreaChart ? "area-view" : "column-view"
      }`}
    >
      <Chart
        key={`${chartKey}-${chartType}`}
        options={options}
        series={series}
        type={isAreaChart ? "area" : "bar"}
        height={450}
      />
    </div>
  );
}

export default DepartmentQualityChart;